import { runInAction, set } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import { NumberInput } from '@feathr/components';

import RecommendationModal from './RecommendationModal';

interface IProps {
  campaign: TTDCampaign;
  errors: string[];
  suppressAlert?: boolean;
  recommendation: Recommendation;
}

function IncreaseBudget({
  campaign,
  errors = [],
  suppressAlert = false,
  recommendation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const recommendedValue =
    recommendation.get('proposed_campaign_values')?.exposure_settings?.target_value ?? 0;
  const recommendedValueFormatted = numeral(recommendedValue).format('$0');

  const prevValue =
    recommendation.get('current_campaign_values')?.exposure_settings?.target_value ?? 0;
  const prevValueFormatted = numeral(prevValue).format('$0');

  useEffect(() => {
    // Add recommended value to the campaign on initial load so the input shows as dirty
    handleTargetValueChange(recommendedValue);
  }, []);

  function handleTargetValueChange(newValue?: number): void {
    const exposureSettings = campaign.get('exposure_settings');
    if (exposureSettings) {
      runInAction((): void => {
        set(exposureSettings, {
          target_value: newValue ?? 0,
        });
      });
      campaign.setAttributeDirty('exposure_settings');
    }
  }

  const alert = t('Increase budget from {{before}} to {{after}}', {
    before: prevValueFormatted,
    after: recommendedValueFormatted,
  });
  const helpText = t('Original budget: {{before}}', {
    before: prevValueFormatted,
  });

  return (
    <>
      {!suppressAlert && <RecommendationModal.Alert description={alert} />}
      {/* TODO: consider a reusable component for this in the wizard that is extended here */}
      <NumberInput
        // TODO: left align the input value when this becomes reusable
        helpPlacement={'bottom'}
        helpText={helpText}
        label={'Budget'}
        min={recommendedValue}
        name={'budget'}
        onChange={handleTargetValueChange}
        prefix={'$'}
        validationError={errors}
        value={campaign.get('exposure_settings')?.target_value}
      />
    </>
  );
}

export default observer(IncreaseBudget);
